import { parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const datePattern = (pattern) => {
  const tokens = { Y: 'yyyy', y: 'yy', m: 'MM', d: 'dd', H: 'HH', M: 'mm', S: 'ss', b: 'MMM', B: 'MMMM', a: 'EEE', A: 'EEEE', z: 'xx', Z: 'zzz', '%': "'%'" };
  return String(pattern).split(/(%.)/).filter(Boolean).map((part) => {
    if (!part.startsWith('%')) return `'${part.replaceAll("'", "''")}'`;
    if (!tokens[part[1]]) throw Error(`Unsupported date format token ${part}.`);
    return tokens[part[1]];
  }).join('');
};
export const lookup = (context, path) => String(path).split('.').reduce((value, key) => value?.[key], context);
export const render = (value, context) => String(value ?? '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (match, key) => {
  const result = lookup(context, key.trim());
  return result == null ? match : typeof result === 'object' ? JSON.stringify(result) : String(result);
});

const renderJson = (value, context) => {
  if (Array.isArray(value)) return value.map((item) => renderJson(item, context));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, renderJson(item, context)]));
  return typeof value === 'string' ? render(value, context) : value;
};

export function simulateAction(node, context) {
  const c = node.config || {}, value = render(c.value, context);
  let output = {}, branch = null, text = '';
  switch (node.type) {
    case 'call_api': {
      const response = context.mock_api_response || { status: 200, ok: true, body: {} };
      const result = { ...response, ok: Number(response.status) >= 200 && Number(response.status) < 300 };
      context[c.output_variable || 'api_response'] = result;
      (c.response_mappings || []).forEach((m) => { if (m.variable) context[m.variable] = lookup(result, m.path) ?? null; });
      branch = result.ok ? 'true' : 'false'; output = result; text = `Mock API response: HTTP ${result.status}. ${result.ok ? 'Success' : 'Failure'} branch.`;
      break;
    }
    case 'manage_tags': {
      const values = render(c.tags, context).split(',').map((v) => v.trim()).filter(Boolean);
      context.tags = c.operation === 'remove' ? (context.tags || []).filter((v) => !values.includes(v)) : [...new Set([...(context.tags || []), ...values])];
      output = { tags: context.tags }; text = `${c.operation === 'remove' ? 'Removed' : 'Added'} tags: ${values.join(', ')}`;
      break;
    }
    case 'broadcast_list': context.broadcast_lists = [...new Set([...(context.broadcast_lists || []), render(c.list_name, context)])]; output = { broadcast_lists: context.broadcast_lists }; text = 'Updated broadcast audience membership.'; break;
    case 'set_attributes':
      context.attributes = { ...(context.attributes || {}) };
      if (c.operation === 'remove') delete context.attributes[c.attribute]; else context.attributes[c.attribute] = value;
      output = { attributes: context.attributes }; text = `Attribute ${c.attribute} ${c.operation === 'remove' ? 'removed' : 'set'}.`; break;
    case 'opt_out': context.opted_out = true; output = { opted_out: true }; text = 'Contact opted out of automated messages.'; break;
    case 'custom_event': output = { event_name: render(c.event_name, context), data: renderJson(JSON.parse(c.payload || '{}'), context) }; text = `Recorded event: ${output.event_name}`; break;
    case 'wait_until': output = { datetime: render(c.datetime, context), timezone: c.timezone }; text = `Skipped wait until ${output.datetime} (${c.timezone}).`; break;
    case 'send_location': output = Object.fromEntries(['latitude', 'longitude', 'name', 'address'].map((k) => [k, render(c[k], context)])); text = `Location: ${output.name} (${output.latitude}, ${output.longitude}) ${output.address}`; break;
    case 'send_catalogue': output = { message: render(c.message, context), product_retailer_id: c.product_retailer_id }; text = `Catalogue message: ${output.message}`; break;
    case 'format_phone': {
      let phone = value.replace(/[\s().-]/g, '').replace(/^00/, '+');
      if (!phone.startsWith('+')) {
        const code = String(c.country_code || '91').replace(/^\+/, '');
        phone = `+${phone.startsWith(code) && phone.length > 10 ? phone : code + phone.replace(/^0+/, '')}`;
      }
      if (!/^\+[1-9]\d{6,14}$/.test(phone)) throw Error('Invalid phone number.');
      context[c.output_variable] = c.output_format === 'digits' ? phone.slice(1) : phone;
      output = { value: context[c.output_variable] }; text = `Saved ${c.output_variable}: ${output.value}`; break;
    }
    case 'format_date': {
      const zone = c.timezone || 'Asia/Kolkata';
      const parsed = c.input_format ? parse(value, datePattern(c.input_format), new Date(2000, 0, 1)) : new Date(value);
      const hasOffset = c.input_format ? /%[zZ]/.test(c.input_format) : /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
      const instant = hasOffset ? parsed : fromZonedTime(parsed, zone);
      const formatted = formatInTimeZone(instant, zone, datePattern(c.output_format || '%Y-%m-%d'));
      context[c.output_variable] = formatted; output = { value: formatted }; text = `Saved ${c.output_variable}: ${formatted}`; break;
    }
    default: return null;
  }
  return { output, branch, text };
}
